import type { NotePreviewBlock } from '../../../notes/notes-coverage';
import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';

/**
 * A page of small text that FILLS the frame and is cut by the frame's edge —
 * never a few lines and then an empty card (QA 2026-10-13: the tile stopped
 * mid-word after ~six lines).
 *
 * Two looks, so a recording's two tiles never read alike (owner, 2026-10-21):
 * a NOTE is a formatted page — `blocks` (`notesPreviewBlocks(json)`), each
 * its own paragraph, headings in bold — and a RECORDING is its transcript as
 * plain running `text`.
 *
 * On a tile the text starts below the kind badge (10 + 32 + 10), where the
 * empty note's grey lines start too; a list row's thumbnail carries no badge
 * and shrinks the type instead (`compact`). One block for web and the app, so
 * the covers cannot drift again.
 */
export function NoteMiniature({
  text,
  blocks,
  compact = false,
}: {
  text?: string;
  blocks?: readonly NotePreviewBlock[];
  compact?: boolean;
}) {
  const body = compact ? 'text-[4px] leading-[6px]' : 'text-[9px] leading-[13px]';
  return (
    <View
      className={cn(
        'absolute inset-0 overflow-hidden',
        compact ? 'px-1 pb-1 pt-1' : 'px-3 pb-3 pt-[52px]',
      )}
      testID="note-miniature">
      {blocks ? (
        blocks.map((block, index) => (
          <Text
            key={index}
            className={cn(
              block.heading
                ? cn(
                    'font-semibold text-secondary-800',
                    compact ? 'text-[5px] leading-[7px]' : 'text-[10px] leading-[14px]',
                  )
                : cn('text-fg-subtle', body),
              index > 0 && (compact ? 'mt-px' : block.heading ? 'mt-2' : 'mt-1'),
            )}>
            {block.text}
          </Text>
        ))
      ) : (
        <Text className={cn('text-fg-subtle', body)}>{text}</Text>
      )}
    </View>
  );
}
(NoteMiniature as { displayName?: string }).displayName = 'NoteMiniature';
